/**
 * vivana-quiz.js (v4) — DOM controller.
 *
 * The question set is merchant-managed (section blocks), so nothing here assumes
 * how many questions exist or what the answers are called:
 *   - the number of scored questions comes from #vivana-questions
 *     [data-total-questions]
 *   - an answer is keyed by the option button's data-q (the question block id)
 *     and valued by its data-value (the option slot)
 *   - the context gate's data-track picks the result track
 *   - the scoring table comes from window.VivanaQuizConfig.scoreMap, emitted by
 *     sections/vivana-quiz.liquid from the same blocks
 *
 * Scoring is delegated to window.VivanaQuizEngine (vivana-quiz-engine.js).
 * User-facing strings come from window.VivanaQuizI18n (declared in the section,
 * sourced from locales/*.json) with English fallbacks, so all copy is
 * translatable via Shopify Translate & Adapt.
 */
(function () {
  'use strict';

  // ─── i18n: read from window.VivanaQuizI18n (locale-backed) with EN fallbacks ───

  function strings() {
    var I = window.VivanaQuizI18n || {};
    var L = I.labels || {};
    return {
      viewProduct:   I.viewProduct   || 'View product',
      atc:           I.atc           || 'Add to Cart',
      atcAdding:     I.atcAdding     || 'Adding…',
      atcAdded:      I.atcAdded      || '✓ Added',
      atcSuccess:    I.atcSuccess    || 'Added to cart!',
      atcError:      I.atcError      || 'Could not add to cart.',
      addAll:        I.addAll        || 'Add Full Routine to Cart',
      addAllAdding:  I.addAllAdding  || 'Adding…',
      addAllDone:    I.addAllDone    || '✓ Routine Added',
      addAllSuccess: I.addAllSuccess || 'Your full routine is in the cart.',
      loadError:     I.loadError     || 'Sorry — the quiz could not load. Please refresh and try again.',
      labels: {
        primarySystem:  L.primarySystem  || 'Primary System',
        runnerSystem:   L.runnerSystem   || 'Also Worth Considering',
        mainProduct:    L.mainProduct    || 'Your Main Product',
        supportProduct: L.supportProduct || 'Your Support Product',
        addon:          L.addon          || 'Recommended Add-on'
      },
      systemWhy: I.systemWhy || {
        A: { primary: 'Covers your highest-scoring needs in one complete daily system.', runner: 'A strong alternative that still targets your top needs.' },
        B: { primary: 'Formulated to complement your HRT.', runner: 'A simpler stack if you prefer fewer products.' }
      },
      productBenefit: I.productBenefit || {
        COR: 'Helps steady stress and cortisol balance.',
        GUT: 'Supports digestion and everyday gut comfort.',
        MAG: 'Foundational support for sleep, muscles and calm.',
        IMM: 'Reinforces immune resilience.',
        COL: 'Supports skin, hair, nails and joints.',
        O3:  'Always included for cardiovascular and cognitive support.'
      }
    };
  }

  // ─── Escaping helpers ──────────────────────────────────────────────────────────
  function escHtml(s) { return String(s == null ? '' : s).replace(/[&<>]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]; }); }
  function escAttr(s) { return escHtml(s).replace(/"/g, '&quot;'); }

  // ─── SVG medal icons ───────────────────────────────────────────────────────────

  var SVG_MEDAL_GOLD = '<svg xmlns="http://www.w3.org/2000/svg" width="56" height="64" viewBox="0 0 56 64" fill="none"><polygon points="14,42 6,60 16,54 20,64 22,46" fill="#C9A800"/><polygon points="42,42 50,60 40,54 36,64 34,46" fill="#C9A800"/><path d="M28,2 L31.5,6.2 L36.8,5 L38.2,10.2 L43.4,11.2 L42.4,16.5 L46.8,19.8 L43.8,24 L46.8,28.2 L42.4,31.5 L43.4,36.8 L38.2,37.8 L36.8,43 L31.5,41.8 L28,46 L24.5,41.8 L19.2,43 L17.8,37.8 L12.6,36.8 L13.6,31.5 L9.2,28.2 L12.2,24 L9.2,19.8 L13.6,16.5 L12.6,11.2 L17.8,10.2 L19.2,5 L24.5,6.2 Z" fill="#F5D025" stroke="#C9A800" stroke-width="1.5"/><circle cx="28" cy="24" r="13" fill="none" stroke="#fff" stroke-width="2.5"/><text x="28" y="30" text-anchor="middle" font-size="18" font-weight="700" fill="#fff" font-family="sans-serif">1</text></svg>';
  var SVG_MEDAL_SILVER = '<svg xmlns="http://www.w3.org/2000/svg" width="56" height="64" viewBox="0 0 56 64" fill="none"><polygon points="14,42 6,60 16,54 20,64 22,46" fill="#808080"/><polygon points="42,42 50,60 40,54 36,64 34,46" fill="#808080"/><path d="M28,2 L31.5,6.2 L36.8,5 L38.2,10.2 L43.4,11.2 L42.4,16.5 L46.8,19.8 L43.8,24 L46.8,28.2 L42.4,31.5 L43.4,36.8 L38.2,37.8 L36.8,43 L31.5,41.8 L28,46 L24.5,41.8 L19.2,43 L17.8,37.8 L12.6,36.8 L13.6,31.5 L9.2,28.2 L12.2,24 L9.2,19.8 L13.6,16.5 L12.6,11.2 L17.8,10.2 L19.2,5 L24.5,6.2 Z" fill="#B0B0B0" stroke="#808080" stroke-width="1.5"/><circle cx="28" cy="24" r="13" fill="none" stroke="#fff" stroke-width="2.5"/><text x="28" y="30" text-anchor="middle" font-size="18" font-weight="700" fill="#fff" font-family="sans-serif">2</text></svg>';
  var SVG_MEDAL_ADDON = '<svg xmlns="http://www.w3.org/2000/svg" width="56" height="64" viewBox="0 0 56 64" fill="none"><polygon points="14,42 6,60 16,54 20,64 22,46" fill="#2E7D32"/><polygon points="42,42 50,60 40,54 36,64 34,46" fill="#2E7D32"/><path d="M28,2 L31.5,6.2 L36.8,5 L38.2,10.2 L43.4,11.2 L42.4,16.5 L46.8,19.8 L43.8,24 L46.8,28.2 L42.4,31.5 L43.4,36.8 L38.2,37.8 L36.8,43 L31.5,41.8 L28,46 L24.5,41.8 L19.2,43 L17.8,37.8 L12.6,36.8 L13.6,31.5 L9.2,28.2 L12.2,24 L9.2,19.8 L13.6,16.5 L12.6,11.2 L17.8,10.2 L19.2,5 L24.5,6.2 Z" fill="#43A047" stroke="#2E7D32" stroke-width="1.5"/><circle cx="28" cy="24" r="13" fill="none" stroke="#fff" stroke-width="2.5"/><line x1="22" y1="24" x2="34" y2="24" stroke="#fff" stroke-width="3" stroke-linecap="round"/><line x1="28" y1="18" x2="28" y2="30" stroke="#fff" stroke-width="3" stroke-linecap="round"/></svg>';

  // Theme editor only: the step whose block is currently selected. Survives the
  // section re-render so init() can restore it, whichever order Shopify fires
  // `shopify:section:load` and `shopify:block:select` in.
  var editorSelectedStep = null;

  // ─── One quiz instance ─────────────────────────────────────────────────────────

  function init(root) {
    if (!root || root.dataset.quizReady === 'true') return;

    var questionsEl = root.querySelector('#vivana-questions');
    if (!questionsEl) return;
    root.dataset.quizReady = 'true';

    var resultsEl    = root.querySelector('#vivana-results');
    var resultsInner = root.querySelector('#vivana-results-inner');
    var addAllWrap   = root.querySelector('#vivana-add-all-wrap');
    var addAllBtn    = root.querySelector('#vivana-add-all-btn');
    var addAllMsg    = root.querySelector('#vivana-add-all-msg');
    var restartBtn   = root.querySelector('#vivana-restart');
    var progressBar  = root.querySelector('#vivana-progress-bar');
    var navWrap      = root.querySelector('#vivana-nav');
    var backBtn      = root.querySelector('#vivana-back');
    var nextBtn      = root.querySelector('#vivana-next');

    var STR = strings();

    // Scored questions are steps 1..lastStep; step 0 is the (unscored) gate.
    var lastStep = parseInt(questionsEl.dataset.totalQuestions, 10);
    if (!lastStep || lastStep < 0) lastStep = 0;

    // answers[questionKey] = optionKey (the gate is stored under '0');
    // gateTrack = the selected gate option's track.
    var answers     = {};
    var gateTrack   = null;
    var currentStep = 0;

    // ─── Step navigation ───────────────────────────────────────────────────────

    function showStep(index) {
      currentStep = index;
      questionsEl.querySelectorAll('.vivana-quiz__step').forEach(function (el) {
        var active = parseInt(el.dataset.step, 10) === index;
        el.dataset.active = active ? 'true' : 'false';
        el.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      updateProgress(index);
      updateNav(index);
      highlightSelected(index);
    }

    function updateProgress(step) {
      if (!progressBar) return;
      var pct = step === 0 || lastStep === 0 ? 0 : Math.round((step / lastStep) * 100);
      progressBar.style.width = pct + '%';
      progressBar.setAttribute('aria-valuenow', pct);
    }

    function updateNav(step) {
      if (!navWrap) return;
      if (step === 0) { navWrap.style.display = 'none'; return; }
      navWrap.style.display = 'flex';
      if (nextBtn) nextBtn.style.display = 'none'; // auto-advance handles progression
    }

    function stepElement(step) {
      return questionsEl.querySelector('.vivana-quiz__step[data-step="' + step + '"]');
    }

    function highlightSelected(step) {
      var stepEl = stepElement(step);
      if (!stepEl) return;
      stepEl.querySelectorAll('.vivana-quiz__option').forEach(function (btn) {
        var saved = answers[btn.dataset.q];
        btn.classList.toggle('is-selected', saved != null && btn.dataset.value === saved);
      });
    }

    // ─── Option click ──────────────────────────────────────────────────────────

    questionsEl.addEventListener('click', function (e) {
      var btn = e.target.closest('.vivana-quiz__option');
      if (!btn || !questionsEl.contains(btn)) return;

      var stepEl = btn.closest('.vivana-quiz__step');
      if (!stepEl) return;
      var step = parseInt(stepEl.dataset.step, 10);

      stepEl.querySelectorAll('.vivana-quiz__option').forEach(function (b) { b.classList.remove('is-selected'); });
      btn.classList.add('is-selected');

      answers[btn.dataset.q] = btn.dataset.value;
      if (step === 0) gateTrack = btn.dataset.track || null;

      if (step < lastStep) {
        showStep(step + 1);
      } else {
        calculateAndRender();
      }
    });

    // ─── Next / Back ───────────────────────────────────────────────────────────

    if (nextBtn) nextBtn.addEventListener('click', function () {
      if (currentStep < lastStep) showStep(currentStep + 1); else calculateAndRender();
    });
    if (backBtn) backBtn.addEventListener('click', function () {
      if (currentStep > 0) showStep(currentStep - 1);
    });

    // ─── Scoring (delegated to engine) ─────────────────────────────────────────

    function calculateAndRender() {
      // Resolved at call time: the engine asset may load after this controller,
      // and the theme editor re-executes both on every section re-render.
      var engine = window.VivanaQuizEngine;
      if (!engine || typeof engine.computeResult !== 'function') {
        resultsInner.innerHTML = '<p class="vivana-quiz__error">' + escHtml(STR.loadError) + '</p>';
        showResults();
        return;
      }

      var scored = {};
      Object.keys(answers).forEach(function (key) {
        if (key !== '0') scored[key] = answers[key];
      });

      var cfg = window.VivanaQuizConfig || {};
      renderResult(engine.computeResult(gateTrack, scored, cfg.scoreMap));
    }

    // ─── Render results ───────────────────────────────────────────────────────

    function showResults() {
      questionsEl.style.display = 'none';
      if (navWrap) navWrap.style.display = 'none';
      resultsEl.style.display = 'block';
    }

    function card(p, key, medal, label, note, extraClass) {
      if (!p) return '';
      return '' +
        '<div class="vivana-result-card' + (extraClass ? ' ' + extraClass : '') + '" data-key="' + key + '">' +
          '<div class="vivana-result-card__medal">' + medal + '</div>' +
          (p.image ? '<img src="' + p.image + '" alt="' + escAttr(p.title) + '" class="vivana-result-card__img" loading="lazy" width="300" height="300">' : '') +
          '<div class="vivana-result-card__body">' +
            '<span class="vivana-result-card__label">' + escHtml(label) + '</span>' +
            '<h4 class="vivana-result-card__title">' + escHtml(p.title) + '</h4>' +
            '<p class="vivana-result-card__price">' + p.price + '</p>' +
            (note ? '<p class="vivana-result-card__why">' + escHtml(note) + '</p>' : '') +
            '<a href="' + p.url + '" class="vivana-result-card__link">' + escHtml(STR.viewProduct) + '</a>' +
            '<button class="vivana-quiz__card-atc-btn button" type="button" data-variant-id="' + p.id + '" data-card-key="' + key + '">' + escHtml(STR.atc) + '</button>' +
            '<p class="vivana-quiz__card-atc-msg" aria-live="polite"></p>' +
          '</div>' +
        '</div>';
    }

    function renderResult(result) {
      showResults();

      var html = '';
      var routineVariants = [];
      var combined = false; // show the "Add full routine" button (Track C only)
      var missing = [];     // recommended keys with no product/system data

      // Build a card, or record the key as missing if its data is null/undefined.
      function addCard(data, key, medal, label, note, extraClass) {
        if (!data) { if (key) missing.push(key); return ''; }
        return card(data, key, medal, label, note, extraClass);
      }

      if (result.outputType === 'systems') {
        var sys = window.VivanaSystems || {};
        var why = STR.systemWhy[result.track] || STR.systemWhy.A;
        html += addCard(sys[result.primary],  result.primary,  SVG_MEDAL_GOLD,   STR.labels.primarySystem, why.primary);
        html += addCard(sys[result.runnerUp], result.runnerUp, SVG_MEDAL_SILVER, STR.labels.runnerSystem,  why.runner);
        // Each system already bundles Omega-3 — no separate add-on card.
      } else {
        var prod = window.VivanaProducts || {};
        html += addCard(prod[result.primary],  result.primary,  SVG_MEDAL_GOLD,   STR.labels.mainProduct,    STR.productBenefit[result.primary]);
        html += addCard(prod[result.runnerUp], result.runnerUp, SVG_MEDAL_SILVER, STR.labels.supportProduct, STR.productBenefit[result.runnerUp]);
        if (result.omega3) {
          html += addCard(prod.O3, 'O3', SVG_MEDAL_ADDON, STR.labels.addon, STR.productBenefit.O3, 'vivana-result-card--addon');
        }
        [result.primary, result.runnerUp].forEach(function (k) { if (prod[k]) routineVariants.push(prod[k].id); });
        if (result.omega3 && prod.O3) routineVariants.push(prod.O3.id);
        combined = routineVariants.length > 1;
      }

      // Defensive guard: surface broken/missing metafield references instead of
      // silently rendering an empty result (e.g. a bundle product was deleted).
      if (missing.length) {
        console.warn('[Vivana Quiz] No data for recommended ' +
          (result.outputType === 'systems' ? 'system(s)' : 'product(s)') + ': ' + missing.join(', ') +
          '. Check the "Find Your Vivana Routine" page metafields (custom.system_* / custom.product_*) — a referenced product may have been deleted or replaced.');
      }
      if (!html) {
        // Nothing could be rendered at all — show a friendly message, not a blank area.
        html = '<p class="vivana-quiz__error">' + escHtml(STR.loadError) + '</p>';
        combined = false;
      }

      resultsInner.innerHTML = html;

      if (addAllWrap) {
        if (combined) {
          addAllWrap.style.display = 'block';
          addAllBtn.dataset.variants = JSON.stringify(routineVariants);
        } else {
          addAllWrap.style.display = 'none';
        }
      }

      resultsInner.querySelectorAll('.vivana-quiz__card-atc-btn').forEach(function (btn) {
        btn.addEventListener('click', function () { handleCardAtc(btn); });
      });
    }

    // ─── Cart helpers ─────────────────────────────────────────────────────────

    function getCartSectionIds() {
      var ids = [];
      document.querySelectorAll('cart-items-component').forEach(function (el) {
        if (el.dataset && el.dataset.sectionId) ids.push(el.dataset.sectionId);
      });
      return ids;
    }

    function dispatchCartUpdate(sections, variantId, quantity) {
      var evt = new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: {}, sourceId: '',
          data: { source: 'vivana-quiz', itemCount: quantity, variantId: String(variantId), sections: sections || {} }
        }
      });
      document.dispatchEvent(evt);
    }

    // ─── Individual card Add to Cart ──────────────────────────────────────────

    function handleCardAtc(btn) {
      var variantId = parseInt(btn.dataset.variantId, 10);
      var msgEl     = btn.nextElementSibling;

      btn.disabled = true;
      btn.textContent = STR.atcAdding;
      if (msgEl) msgEl.textContent = '';

      var sectionIds = getCartSectionIds();
      var body = { items: [{ id: variantId, quantity: 1 }] };
      if (sectionIds.length) body.sections = sectionIds.join(',');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || STR.atcError);
          btn.textContent = STR.atcAdded;
          if (msgEl) { msgEl.textContent = STR.atcSuccess; msgEl.className = 'vivana-quiz__card-atc-msg vivana-quiz__atc-msg--success'; }
          dispatchCartUpdate(data.sections, variantId, 1);
        });
      }).catch(function (err) {
        btn.disabled = false;
        btn.textContent = STR.atc;
        if (msgEl) { msgEl.textContent = err.message; msgEl.className = 'vivana-quiz__card-atc-msg vivana-quiz__atc-msg--error'; }
      });
    }

    // ─── "Add full routine" (Track C) ─────────────────────────────────────────

    if (addAllBtn) addAllBtn.addEventListener('click', function () {
      var variants = JSON.parse(addAllBtn.dataset.variants || '[]');
      if (!variants.length) return;

      addAllBtn.disabled = true;
      addAllBtn.textContent = STR.addAllAdding;
      if (addAllMsg) addAllMsg.textContent = '';

      var sectionIds = getCartSectionIds();
      var body = { items: variants.map(function (id) { return { id: id, quantity: 1 }; }) };
      if (sectionIds.length) body.sections = sectionIds.join(',');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || STR.atcError);
          addAllBtn.textContent = STR.addAllDone;
          if (addAllMsg) { addAllMsg.textContent = STR.addAllSuccess; addAllMsg.className = 'vivana-quiz__atc-msg vivana-quiz__atc-msg--success'; }
          resultsInner.querySelectorAll('.vivana-quiz__card-atc-btn').forEach(function (btn) { btn.textContent = STR.atcAdded; btn.disabled = true; });
          dispatchCartUpdate(data.sections, variants[0], variants.length);
        });
      }).catch(function (err) {
        addAllBtn.disabled = false;
        addAllBtn.textContent = STR.addAll;
        if (addAllMsg) { addAllMsg.textContent = err.message; addAllMsg.className = 'vivana-quiz__atc-msg vivana-quiz__atc-msg--error'; }
      });
    });

    // ─── Restart ──────────────────────────────────────────────────────────────

    function restart() {
      answers = {};
      gateTrack = null;
      currentStep = 0;
      resultsEl.style.display = 'none';
      questionsEl.style.display = 'block';
      resultsInner.innerHTML = '';
      // Clear stale selection highlight on ALL steps (not just the active one),
      // so hidden steps don't retain .is-selected after a restart.
      questionsEl.querySelectorAll('.vivana-quiz__option.is-selected').forEach(function (el) {
        el.classList.remove('is-selected');
      });
      if (addAllWrap) addAllWrap.style.display = 'none';
      if (addAllMsg)  { addAllMsg.textContent = ''; addAllMsg.className = 'vivana-quiz__atc-msg'; }
      if (addAllBtn)  { addAllBtn.disabled = false; addAllBtn.textContent = STR.addAll; delete addAllBtn.dataset.variants; }
      showStep(0);
    }

    if (restartBtn) restartBtn.addEventListener('click', restart);

    // Theme editor: jump to the step whose block the merchant just selected, so
    // the question being edited is the one on screen.
    root.addEventListener('vivana-quiz:show-step', function (e) {
      if (resultsEl && resultsEl.style.display === 'block') restart();
      showStep(e.detail.step);
    });

    showStep(editorSelectedStep == null ? 0 : editorSelectedStep);
  }

  // ─── Boot / re-boot ───────────────────────────────────────────────────────────

  function boot(scope) {
    (scope || document).querySelectorAll('.vivana-quiz').forEach(init);
  }

  // The theme editor replaces the section's markup on every settings change
  // without a page reload, which discards the DOM this controller was bound to.
  // Re-initialise on section load, and follow block selection. Registered once,
  // even though the section re-executes this asset on each re-render.
  if (!window.VivanaQuizBooted) {
    window.VivanaQuizBooted = true;

    document.addEventListener('shopify:section:load', function (e) {
      boot(e.target);
    });

    document.addEventListener('shopify:block:select', function (e) {
      var stepEl = e.target.closest ? e.target.closest('.vivana-quiz__step') : null;
      if (!stepEl) return;
      var root = stepEl.closest('.vivana-quiz');
      if (!root) return;
      editorSelectedStep = parseInt(stepEl.dataset.step, 10) || 0;
      root.dispatchEvent(new CustomEvent('vivana-quiz:show-step', {
        detail: { step: editorSelectedStep }
      }));
    });

    document.addEventListener('shopify:block:deselect', function (e) {
      if (e.target.closest && e.target.closest('.vivana-quiz__step')) editorSelectedStep = null;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
})();
